import { db } from "../db/database";
import type { AppSettings, AuditLog, ImportBatch, InventoryBalance, InventoryMovement, MarketplaceListing, MarketplaceVariantListing, PendingSync, Product, ProductImportBatch, ProductVariant, VariantAlias } from "../types";
import { apiRequest } from "./api";
import { ensureSyncSession } from "./auth";

interface PushResponse {
  results: Array<{ id: string; status: "sincronizado" | "conflito" | "erro"; message?: string }>;
  cursor?: string;
}

interface PullResponse {
  cursor: string;
  products?: Product[];
  variants?: ProductVariant[];
  balances?: InventoryBalance[];
  movements?: InventoryMovement[];
  imports?: ImportBatch[];
  variantAliases?: VariantAlias[];
  marketplaceListings?: MarketplaceListing[];
  marketplaceVariants?: MarketplaceVariantListing[];
  productImports?: ProductImportBatch[];
  auditLogs?: AuditLog[];
  settings?: Partial<AppSettings>;
}

let running = false;

export async function syncNow(): Promise<{ pushed: number; conflicts: number }> {
  if (running || !navigator.onLine) return { pushed: 0, conflicts: 0 };
  const settings = await db.settings.get("app");
  if (!settings?.apiUrl) return { pushed: 0, conflicts: 0 };
  const session = await ensureSyncSession();
  if (!session?.accessToken) return { pushed: 0, conflicts: 0 };
  running = true;
  let pushed = 0;
  let conflicts = 0;

  try {
    const pending = await db.pendingSync.where("status").anyOf("pendente", "erro").sortBy("createdAt");
    if (pending.length) {
      await db.pendingSync.bulkPut(pending.map((item) => ({ ...item, status: "sincronizando", attempts: item.attempts + 1, updatedAt: new Date().toISOString() })));
      let response: PushResponse;
      try {
        response = await apiRequest<PushResponse>("/sync/push", { method: "POST", body: JSON.stringify({ operations: pending }) });
      } catch (error) {
        const message = error instanceof Error ? error.message : "Falha ao sincronizar.";
        await db.pendingSync.bulkPut(pending.map((item) => ({ ...item, status: "erro", attempts: item.attempts + 1, lastError: message, updatedAt: new Date().toISOString() })));
        throw error;
      }
      for (const result of response.results) {
        const operation = await db.pendingSync.get(result.id);
        if (!operation) continue;
        const status: PendingSync["status"] = result.status;
        await db.pendingSync.update(result.id, { status, lastError: result.message, updatedAt: new Date().toISOString() });
        if (status === "sincronizado") {
          pushed += 1;
          await db.movements.where("operationId").equals(result.id).modify({ syncStatus: "sincronizado" });
        }
        if (status === "conflito") {
          conflicts += 1;
          await db.movements.where("operationId").equals(result.id).modify({ syncStatus: "conflito" });
        }
      }
    }

    const cursor = (await db.kv.get("sync_cursor"))?.value;
    const query = typeof cursor === "string" ? `?cursor=${encodeURIComponent(cursor)}` : "";
    const pull = await apiRequest<PullResponse>(`/sync/pull${query}`);
    await db.transaction("rw", [db.products, db.variants, db.balances, db.movements, db.imports, db.auditLogs, db.settings, db.kv, db.variantAliases, db.marketplaceListings, db.marketplaceVariants, db.productImports], async () => {
      if (pull.products?.length) await db.products.bulkPut(pull.products);
      if (pull.variants?.length) await db.variants.bulkPut(pull.variants);
      if (pull.balances?.length) await db.balances.bulkPut(pull.balances);
      if (pull.movements?.length) await db.movements.bulkPut(pull.movements);
      if (pull.imports?.length) await db.imports.bulkPut(pull.imports);
      if (pull.variantAliases?.length) await db.variantAliases.bulkPut(pull.variantAliases);
      if (pull.marketplaceListings?.length) await db.marketplaceListings.bulkPut(pull.marketplaceListings);
      if (pull.marketplaceVariants?.length) await db.marketplaceVariants.bulkPut(pull.marketplaceVariants);
      if (pull.productImports?.length) await db.productImports.bulkPut(pull.productImports);
      if (pull.auditLogs?.length) await db.auditLogs.bulkPut(pull.auditLogs);
      if (pull.settings) await db.settings.update("app", pull.settings);
      await db.kv.put({ key: "sync_cursor", value: pull.cursor });
      await db.settings.update("app", { lastSync: new Date().toISOString() });
    });
    return { pushed, conflicts };
  } finally {
    running = false;
  }
}

export function installAutoSync(onStatus?: (status: "sincronizando" | "concluido" | "erro") => void) {
  const attempt = async () => {
    if (!navigator.onLine) return;
    onStatus?.("sincronizando");
    try {
      await syncNow();
      onStatus?.("concluido");
    } catch {
      onStatus?.("erro");
    }
  };
  window.addEventListener("online", attempt);
  const timer = window.setInterval(attempt, 60_000);
  void attempt();
  return () => {
    window.removeEventListener("online", attempt);
    window.clearInterval(timer);
  };
}
